import { inject } from '@angular/core';
import { signalStoreFeature, withMethods } from '@ngrx/signals';
import { entities } from 'src/shared/services/client/client';
import { EntitlementLookupCacheDbService } from './entitlement-lookup-cache-db.service';

/** Cached entitlement lookup result for an email, with optional membership-found screen flag. */
export interface CachedLookupEntry {
  email: string;
  cachedAt: number;
  hasActiveKcMembership: boolean;
  kcPackages: entities.ActivePackageResponse[];
  /** True when the user has seen the membership-found screen. */
  membershipFoundScreenSeen?: boolean;
}

/**
 * NgRx Signal Store feature for persisting entitlement lookup results by email.
 * Uses IndexedDB via {@link EntitlementLookupCacheDbService}. Exposes getCached, setCached,
 * updateCachedFromLookup, and markMembershipFoundScreenSeen.
 */
export function withEntitlementLookupCache() {
  return signalStoreFeature(
    withMethods((store, cacheDb = inject(EntitlementLookupCacheDbService)) => ({
      /** Returns the cached entry for the email, or null if missing or stale. */
      async getCached(email: string): Promise<CachedLookupEntry | null> {
        return await cacheDb.getByKey<CachedLookupEntry>(email);
      },

      /** Writes a full cache entry for the email (cachedAt/invalidatesAt set automatically). */
      async setCached(
        email: string,
        data: Omit<CachedLookupEntry, 'email' | 'cachedAt' | 'invalidatesAt'>
      ): Promise<void> {
        const now = Date.now();
        const entry: CachedLookupEntry = {
          ...data,
          email,
          cachedAt: now,
        };
        await cacheDb.putByKey(email, entry);
      },

      /**
       * Updates the cache with fresh lookup data. Preserves existing properties (e.g. membershipFoundScreenSeen)
       * when rechecking after invalidation. Use after a fresh API lookup.
       */
      async updateCachedFromLookup(
        email: string,
        data: Pick<CachedLookupEntry, 'hasActiveKcMembership' | 'kcPackages'>
      ): Promise<CachedLookupEntry> {
        const existing = await cacheDb.getByKey<CachedLookupEntry>(email);
        const now = Date.now();
        const entry: CachedLookupEntry = {
          ...existing,
          ...data,
          email,
          cachedAt: now,
        };
        await cacheDb.putByKey(email, entry);
        return entry;
      },

      /** Sets membershipFoundScreenSeen to true for the email (creates a minimal entry if none exists). */
      async markMembershipFoundScreenSeen(email: string): Promise<void> {
        const entry = await cacheDb.getByKey<CachedLookupEntry>(email);
        const now = Date.now();
        const updated: CachedLookupEntry = entry
          ? { ...entry, membershipFoundScreenSeen: true }
          : {
              email,
              cachedAt: now,
              hasActiveKcMembership: false,
              kcPackages: [],
              membershipFoundScreenSeen: true,
            };
        await cacheDb.putByKey(email, updated);
      },
    }))
  );
}
